import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { postDonationSession } from "../services/AnimalSplitCore";
import SelectContributor from "./SelectContributor";
import "../containers/EditTransactionTheme.css";

export default function GroupDonationInitiatorModal({
  modalId,
  groupId,
  groupName,
  contributors = [],
  donationType = "group_settle", // 'group_settle' (create txn + settle) or 'group_donate' (donation only)
  isCreatingDonation,
  setIsCreatingDonation,
}) {
  const { t } = useTranslation();
  const [contributorId, setContributorId] = useState("");
  const [amountCents, setAmountCents] = useState(100); // default $1

  const campaignYear = useMemo(() => new Date().getFullYear(), []);
  const amountUsd = useMemo(() => Math.round((Number(amountCents) || 0) / 100), [amountCents]);
  const truncatedGroupName = useMemo(() => {
    if (!groupName) return "Anonymous";
    return groupName.length > 10 ? `${groupName.substring(0, 10)}...` : groupName;
  }, [groupName]);

  useEffect(() => {
    if (!contributorId && contributors.length > 0) {
      setContributorId(contributors[0].id);
    }
  }, [contributors, contributorId]);

  const handleConfirm = useCallback(async () => {
    if (isCreatingDonation) return;
    if (!contributorId) {
      alert(t("group.select_contributor_hint") || "Please select who is initiating the group donation.");
      return;
    }

    try {
      setIsCreatingDonation(true);

      const session = await postDonationSession({
        amount: amountCents,
        alias: truncatedGroupName,
        note: `support animalsplit ${campaignYear}`,
        returnUrl: window.location.href,
        groupId,
        donationType,
        contributorId,
      });

      if (!session?.url) throw new Error("Invalid session response");

      try {
        localStorage.setItem("hasDonated", "true");
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem("lastReturnDonateShown", today);
      } catch (e) {
        // ignore
      }

      window.location.href = session.url;
    } catch (error) {
      console.error("Create donation & settle failed:", error);
      alert(t("donation.payment_failed") || "Unable to initiate payment. Please try again.");
      setIsCreatingDonation(false);
    }
  }, [
    amountCents,
    campaignYear,
    contributorId,
    donationType,
    groupId,
    isCreatingDonation,
    setIsCreatingDonation,
    t,
    truncatedGroupName,
  ]);

  const title = useMemo(() => {
    if (donationType === "group_settle") {
      return t("group.settle_create_donation_and_settle") || "Donate as group & settle";
    }
    return t("general.donate") || "Donate";
  }, [donationType, t]);

  const confirmLabel = useMemo(() => {
    if (donationType === "group_settle") {
      return (
        t("group.group_donation_and_settle_cta_amount", { amount: amountUsd }) ||
        `Donate as group $${amountUsd} & settle`
      );
    }
    return (
      t("group.group_donate_cta_amount", { amount: amountUsd }) ||
      `Donate $${amountUsd}`
    );
  }, [amountUsd, donationType, t]);

  const modal = (
    <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-theme as-modal as-transaction-modal">
          <div className="modal-header">
            <div className="as-transaction-modal-header">
              <div className="as-transaction-modal-title">
                <h5 className="modal-title">{title}</h5>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t("general.close") || "Close"}
                disabled={isCreatingDonation}
              />
            </div>
          </div>

          <div className="modal-body">
            <p className="mb-2">
              {t("group.settle_donation_initiator") || "Who is initiating the group donation?"}
            </p>

            <div className="as-tx-control">
              <SelectContributor
                contributors={contributors}
                contributorId={contributorId}
                setContributorId={setContributorId}
                editMode={true}
              />
            </div>

            <div className="mt-3">
              <div className="small text-muted mb-2" style={{ fontSize: "12px" }}>
                {t("donation.amount") || "Amount"}
              </div>
              <div className="btn-group" role="group" aria-label={t("donation.amount") || "Amount"}>
                <button
                  type="button"
                  className={`btn btn-sm ${amountCents === 100 ? "as-btn-primary-gradient" : "as-btn-soft"}`}
                  onClick={() => setAmountCents(100)}
                  disabled={isCreatingDonation}
                >
                  $1
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${amountCents === 500 ? "as-btn-primary-gradient" : "as-btn-soft"}`}
                  onClick={() => setAmountCents(500)}
                  disabled={isCreatingDonation}
                >
                  $5
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${amountCents === 1000 ? "as-btn-primary-gradient" : "as-btn-soft"}`}
                  onClick={() => setAmountCents(1000)}
                  disabled={isCreatingDonation}
                >
                  $10
                </button>
              </div>
            </div>

            {donationType === "group_settle" && (
              <p className="mt-3 mb-0 text-muted" style={{ fontSize: "12px" }}>
                {t("group.settle_donate_txn_hint") ||
                  "Tip: set title to \"Donate $1\", amount to $1.00, and split evenly."}
              </p>
            )}
          </div>

          <div className="modal-footer as-modal-footer">
            <button
              type="button"
              className="btn btn-sm as-btn-soft"
              data-bs-dismiss="modal"
              disabled={isCreatingDonation}
            >
              {t("general.cancel") || "Cancel"}
            </button>
            <button
              type="button"
              className="btn btn-sm as-btn-primary-gradient"
              onClick={handleConfirm}
              disabled={isCreatingDonation}
            >
              {isCreatingDonation
                ? (t("donation.redirecting") || "Redirecting…")
                : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

